import React from "react";
import { InlineMath } from "react-katex";

export const GIVEN_ANGLE_COLOR = "#f59e0b";
export const SOUGHT_ANGLE_COLOR = "#8b5cf6";

const toRadians = (degrees) => (degrees * Math.PI) / 180;

function polar(center, radius, degrees) {
  const angle = toRadians(degrees);
  return {
    x: center.x + radius * Math.cos(angle),
    y: center.y + radius * Math.sin(angle),
  };
}

function normalizeAngleDifference(value) {
  let result = value;
  while (result <= -Math.PI) result += 2 * Math.PI;
  while (result > Math.PI) result -= 2 * Math.PI;
  return result;
}

function angleArc(vertex, first, second, radius) {
  const startAngle = Math.atan2(first.y - vertex.y, first.x - vertex.x);
  const endAngle = Math.atan2(second.y - vertex.y, second.x - vertex.x);
  const difference = normalizeAngleDifference(endAngle - startAngle);
  const start = {
    x: vertex.x + radius * Math.cos(startAngle),
    y: vertex.y + radius * Math.sin(startAngle),
  };
  const end = {
    x: vertex.x + radius * Math.cos(startAngle + difference),
    y: vertex.y + radius * Math.sin(startAngle + difference),
  };

  return {
    path: `M ${start.x} ${start.y} A ${radius} ${radius} 0 0 ${difference >= 0 ? 1 : 0} ${end.x} ${end.y}`,
    labelAngle: startAngle + difference / 2,
  };
}

export function buildFourPointCircleModel({
  inscribedDeg,
  centralDeg,
  size = 360,
  radius = 122,
}) {
  const center = { x: size / 2, y: size / 2 + 4 };
  const dAngle = 270;
  const bAngle = 90;
  const cAngle = bAngle - centralDeg;
  const aAngle = cAngle + 2 * inscribedDeg;

  return {
    size,
    radius,
    O: center,
    A: polar(center, radius, aAngle),
    B: polar(center, radius, bAngle),
    C: polar(center, radius, cAngle),
    D: polar(center, radius, dAngle),
  };
}

function MathLabel({ x, y, math, color = "currentColor", width = 64, fontSize = 17 }) {
  return (
    <foreignObject x={x - width / 2} y={y - 15} width={width} height="30">
      <div style={{ color, fontSize, lineHeight: 1.2, textAlign: "center" }}>
        <InlineMath math={math} />
      </div>
    </foreignObject>
  );
}

function outsideLabel(point, center, distance = 20) {
  const dx = point.x - center.x;
  const dy = point.y - center.y;
  const length = Math.hypot(dx, dy) || 1;
  return {
    x: point.x + (dx / length) * distance,
    y: point.y + (dy / length) * distance,
  };
}

function angleLabel(vertex, arc, distance) {
  return {
    x: vertex.x + distance * Math.cos(arc.labelAngle),
    y: vertex.y + distance * Math.sin(arc.labelAngle),
  };
}

export default function MaturaFourPointCircleAnglesDiagram({
  inscribedDeg,
  centralDeg,
  resultDeg,
  size = 360,
}) {
  const model = buildFourPointCircleModel({ inscribedDeg, centralDeg, size });
  const { O, A, B, C, D, radius } = model;
  const inscribedArc = angleArc(D, C, A, 34);
  const centralArc = angleArc(O, C, B, 34);
  const soughtArc = angleArc(O, B, A, 48);
  const inscribedLabel = angleLabel(D, inscribedArc, 57);
  // Podpis zostaje na dwusiecznej kąta COB. Dla wąskiego kąta odsuwamy go
  // wyłącznie dalej od O — nie w bok — dzięki czemu pozostaje między ramionami.
  const centralLabelDistance = 57 + Math.max(0, 35 - centralDeg) * (25 / 15);
  const centralLabel = angleLabel(O, centralArc, centralLabelDistance);
  const soughtLabel = angleLabel(O, soughtArc, 72);
  const labels = {
    A: outsideLabel(A, O),
    B: outsideLabel(B, O),
    C: outsideLabel(C, O),
    D: outsideLabel(D, O, 18),
  };

  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label="Okrąg z kątem wpisanym i kątami środkowymi"
      style={{ width: "min(100%, 340px)", height: "auto", margin: "0 auto", display: "block" }}
    >
      <circle cx={O.x} cy={O.y} r={radius} fill="none" stroke="currentColor" strokeWidth="2.2" />
      <path d={`M ${D.x} ${D.y} L ${A.x} ${A.y}`} fill="none" stroke="currentColor" strokeWidth="2" />
      <path d={`M ${D.x} ${D.y} L ${C.x} ${C.y}`} fill="none" stroke="currentColor" strokeWidth="2" />
      <path
        d={`M ${D.x} ${D.y} L ${B.x} ${B.y}`}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeDasharray="6 5"
        opacity="0.72"
      />
      <path d={`M ${O.x} ${O.y} L ${A.x} ${A.y}`} fill="none" stroke="currentColor" strokeWidth="2" />
      <path d={`M ${O.x} ${O.y} L ${C.x} ${C.y}`} fill="none" stroke="currentColor" strokeWidth="2" />

      <path data-angle="given-inscribed" d={inscribedArc.path} fill="none" stroke={GIVEN_ANGLE_COLOR} strokeWidth="4" strokeLinecap="round" />
      <path data-angle="given-central" d={centralArc.path} fill="none" stroke={GIVEN_ANGLE_COLOR} strokeWidth="4" strokeLinecap="round" />
      <path data-angle="sought" d={soughtArc.path} fill="none" stroke={SOUGHT_ANGLE_COLOR} strokeWidth="4" strokeLinecap="round" />

      {["A", "B", "C", "D"].map((name) => (
        <circle key={name} data-point={name} cx={model[name].x} cy={model[name].y} r="3.7" fill="currentColor" />
      ))}
      <circle data-point="O" cx={O.x} cy={O.y} r="3.7" fill="currentColor" />

      {["A", "B", "C", "D"].map((name) => (
        <MathLabel key={name} x={labels[name].x} y={labels[name].y} math={name} />
      ))}
      <MathLabel x={O.x - 11} y={O.y - 12} math="O" />
      <MathLabel x={inscribedLabel.x} y={inscribedLabel.y} math={`${inscribedDeg}^{\\circ}`} color={GIVEN_ANGLE_COLOR} />
      <MathLabel x={centralLabel.x} y={centralLabel.y} math={`${centralDeg}^{\\circ}`} color={GIVEN_ANGLE_COLOR} />
      <MathLabel x={soughtLabel.x} y={soughtLabel.y} math="?" color={SOUGHT_ANGLE_COLOR} width={34} fontSize={19} />
      <title>{`Kąt wpisany ${inscribedDeg}°, kąt środkowy ${centralDeg}°, szukany kąt ${resultDeg}°`}</title>
    </svg>
  );
}
